/**
 * MenuBarItems — Active window name + macOS-style app menu with dropdowns
 * File/Edit/View menus change based on active window
 * Rule 1: MAX 300 lines
 */
import { Briefcase, Home, Palette } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL, apiFetch } from '../../../config/api';
import { useWindowManager } from '../WindowManager/WindowManagerContext';
import { WINDOW_MENUS, type MenuItem } from './window-menus'

interface Workspace { id: number; name: string; icon: string; color: string }
interface Props { onMenuClick?: (item: string) => void }

const WORKSPACE_ICONS: Record<string, React.ReactNode> = {
  Briefcase: <Briefcase size={11} />,
  Home: <Home size={11} />,
  Palette: <Palette size={11} />,
}

function dispatchMenuAction(action: string, activeWindowId?: string) {
  const dispatch = (event: string, detail?: unknown) =>
    window.dispatchEvent(new CustomEvent(event, detail !== undefined ? { detail } : undefined))

  switch (action) {
    // OS
    case 'new-window': dispatch('runai:open-window', 'launchpad'); break
    case 'close-window': if (activeWindowId) dispatch('runai:close-window', activeWindowId); break
    case 'minimize-window': if (activeWindowId) dispatch('runai:minimize-window', activeWindowId); break
    case 'maximize-window': if (activeWindowId) dispatch('runai:maximize-window', activeWindowId); break
    case 'open-settings': dispatch('runai:open-window', 'os-settings'); break
    case 'open-agents': dispatch('runai:open-window', 'agents-window'); break
    case 'open-flzhub': dispatch('runai:open-window', 'flzhub'); break
    case 'open-store': dispatch('runai:open-window', 'store'); break
    case 'open-skills': dispatch('runai:open-window', 'skills-window'); break
    case 'open-task-manager': dispatch('runai:open-window', 'task-manager'); break
    case 'open-social': dispatch('runai:open-window', 'social-window'); break
    case 'open-research': dispatch('runai:open-window', 'research-window'); break
    case 'open-brain': dispatch('runai:open-window', 'neural-brain'); break
    case 'open-ask-ai': dispatch('runai:open-window', 'ask-ai'); break
    // RunBrowser
    case 'browser-reload': dispatch('runai:browser-reload'); break
    case 'browser-bookmark': dispatch('runai:browser-bookmark', { url: '', title: '' }); break
    case 'browser-summarize': dispatch('runai:browser-summarize'); break
    case 'browser-new-tab': dispatch('runai:browser-new-tab'); break
    case 'browser-reader-mode': dispatch('runai:browser-reader-mode'); break
    case 'browser-history': dispatch('runai:browser-history'); break
    case 'browser-save-to-files': dispatch('runai:browser-save-to-files'); break
    // RunFiles
    case 'files-upload': dispatch('runai:files-upload'); break
    case 'files-refresh': dispatch('runai:files-refresh'); break
    case 'files-new-folder': dispatch('runai:files-new-folder'); break
    case 'files-select-all': dispatch('runai:files-select-all'); break
    case 'files-trash-selected': dispatch('runai:files-trash-selected'); break
    case 'files-toggle-hidden': dispatch('runai:files-toggle-hidden'); break
    case 'files-icon-view': dispatch('runai:files-view', 'icon'); break
    case 'files-list-view': dispatch('runai:files-view', 'list'); break
    case 'files-go-documents': dispatch('runai:files-open-folder', { folder: 'documents' }); break
    case 'files-go-downloads': dispatch('runai:files-open-folder', { folder: 'downloads' }); break
    case 'files-go-images': dispatch('runai:files-open-folder', { folder: 'images' }); break
    case 'files-go-music': dispatch('runai:files-open-folder', { folder: 'music' }); break
    case 'files-go-videos': dispatch('runai:files-open-folder', { folder: 'videos' }); break
    case 'files-go-code': dispatch('runai:files-open-folder', { folder: 'code' }); break
    case 'files-go-projects': dispatch('runai:files-open-folder', { folder: 'projects' }); break
    case 'files-go-trash': dispatch('runai:files-open-folder', { folder: 'trash' }); break
    // RunCode
    case 'runcode-save': dispatch('runai:code-save'); break
    case 'runcode-run': dispatch('runai:code-run'); break
    case 'runcode-terminal': dispatch('runai:code-toggle-terminal'); break
    case 'runcode-ai-review': dispatch('runai:code-ai-review'); break
    case 'runcode-diff': dispatch('runai:code-toggle-diff'); break
    // RunPlayer
    case 'player-open': dispatch('runai:files-open-folder', { folder: 'music' }); break
    case 'player-play-pause': dispatch('runai:player-play-pause'); break
    case 'player-next': dispatch('runai:player-next'); break
    case 'player-prev': dispatch('runai:player-prev'); break
    case 'player-shuffle': dispatch('runai:player-shuffle'); break
    case 'player-repeat': dispatch('runai:player-repeat'); break
    case 'player-toggle-playlist': dispatch('runai:player-toggle-playlist'); break
    case 'player-mini': dispatch('runai:player-mini'); break
    case 'player-open-studio': dispatch('runai:player-open-studio'); break
    // RunSheet
    case 'sheet-new': dispatch('runai:sheet-new'); break
    case 'sheet-save': dispatch('runai:sheet-save'); break
    case 'sheet-export-xlsx': dispatch('runai:sheet-export', 'xlsx'); break
    case 'sheet-export-csv': dispatch('runai:sheet-export', 'csv'); break
    case 'sheet-ai-formula': dispatch('runai:sheet-ai', 'formula'); break
    case 'sheet-ai-analyze': dispatch('runai:sheet-ai', 'analyze'); break
    case 'sheet-ai-chart': dispatch('runai:sheet-ai', 'chart'); break
    // RunDocs
    case 'docs-new': dispatch('runai:docs-new'); break
    case 'docs-save': dispatch('runai:docs-save'); break
    case 'docs-export-docx': dispatch('runai:docs-export', 'docx'); break
    case 'docs-export-pdf': dispatch('runai:docs-export', 'pdf'); break
    case 'docs-bold': dispatch('runai:docs-format', 'bold'); break
    case 'docs-italic': dispatch('runai:docs-format', 'italic'); break
    case 'docs-underline': dispatch('runai:docs-format', 'underline'); break
    case 'docs-ai-grammar': dispatch('runai:docs-ai', 'grammar'); break
    case 'docs-ai-summarize': dispatch('runai:docs-ai', 'summarize'); break
    case 'docs-ai-expand': dispatch('runai:docs-ai', 'expand'); break
    case 'docs-ai-translate': dispatch('runai:docs-ai', 'translate'); break
    // Trash
    case 'trash-empty': dispatch('runai:trash-empty'); break
    case 'trash-restore-selected': dispatch('runai:trash-restore-selected'); break
    case 'trash-restore-all': dispatch('runai:trash-restore-all'); break
    case 'trash-delete-permanent': dispatch('runai:trash-delete-permanent'); break
    // RunCanvas
    case 'canvas-new': dispatch('runai:canvas-new'); break
    case 'canvas-save': dispatch('runai:canvas-save'); break
    case 'canvas-export-pdf': dispatch('runai:canvas-export', 'pdf'); break
    case 'canvas-export-png': dispatch('runai:canvas-export', 'png'); break
    case 'canvas-export-pptx': dispatch('runai:canvas-export', 'pptx'); break
    case 'canvas-undo': dispatch('runai:canvas-undo'); break
    case 'canvas-redo': dispatch('runai:canvas-redo'); break
    case 'canvas-select-all': dispatch('runai:canvas-select-all'); break
    case 'canvas-ai-image': dispatch('runai:canvas-ai', 'image'); break
    case 'canvas-add-text': dispatch('runai:canvas-ai', 'text'); break
    case 'canvas-ai-layout': dispatch('runai:canvas-ai', 'layout'); break
    // RunCut
    case 'cut-new': dispatch('runai:cut-new'); break
    case 'cut-open': dispatch('runai:cut-open'); break
    case 'cut-save': dispatch('runai:cut-save'); break
    case 'cut-export': dispatch('runai:cut-export'); break
    case 'cut-undo': dispatch('runai:cut-undo'); break
    case 'cut-redo': dispatch('runai:cut-redo'); break
    case 'cut-split': dispatch('runai:cut-split'); break
    case 'cut-clip-cut': dispatch('runai:cut-clip', 'cut'); break
    case 'cut-clip-copy': dispatch('runai:cut-clip', 'copy'); break
    case 'cut-clip-paste': dispatch('runai:cut-clip', 'paste'); break
    case 'cut-ai-generate': dispatch('runai:cut-ai', 'generate'); break
    case 'cut-ai-subtitles': dispatch('runai:cut-ai', 'subtitles'); break
    case 'cut-ai-color-grade': dispatch('runai:cut-ai', 'color-grade'); break
    case 'cut-ai-remove-bg': dispatch('runai:cut-ai', 'remove-bg'); break
    // RunTerminal
    case 'terminal-new': dispatch('runai:open-window', 'run-terminal'); break
    case 'terminal-clear': dispatch('runai:terminal-clear'); break
    case 'terminal-copy': document.execCommand('copy'); break
    case 'terminal-paste': dispatch('runai:terminal-paste'); break
    case 'terminal-explain': dispatch('runai:terminal-explain'); break
    case 'terminal-fix': dispatch('runai:terminal-fix'); break
    case 'terminal-ai-run': dispatch('runai:terminal-ai-run'); break
    // RunMusic
    case 'music-generate': dispatch('runai:music-generate'); break
    default: break
  }
}

export default function MenuBarItems({ onMenuClick }: Props) {
  const { windows, activeWindowId } = useWindowManager()
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [activeWs, setActiveWs] = useState('Work')
  const [wsOpen, setWsOpen] = useState(false)
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const wsRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const activeWindow = windows.find(w => w.id === activeWindowId)
  const menus = (activeWindowId && WINDOW_MENUS[activeWindowId]) ? WINDOW_MENUS[activeWindowId] : WINDOW_MENUS.default

  useEffect(() => {
    apiFetch(`${API_URL}/api/workspaces`).then(r => r.json())
      .then((d: { workspaces?: Workspace[] }) => {
        if (d.workspaces?.length) {
          setWorkspaces(d.workspaces)
          const def = d.workspaces.find(w => (w as any).is_default)
          if (def) setActiveWs(def.name)
        }
      })
      .catch(() => setWorkspaces([
        { id: 0, name: 'Work', icon: 'Briefcase', color: '#f59e0b' },
        { id: 0, name: 'Personal', icon: 'Home', color: '#6366f1' },
        { id: 0, name: 'Creative', icon: 'Palette', color: '#ec4899' },
      ]))
  }, [])

  useEffect(() => {
    const h = (e: Event) => { const { name } = (e as CustomEvent).detail ?? {}; if (name) setActiveWs(name) }
    window.addEventListener('runai:switch-workspace', h)
    return () => window.removeEventListener('runai:switch-workspace', h)
  }, [])

  useEffect(() => {
    if (!wsOpen && !openMenu) return
    const h = (e: MouseEvent) => {
      if (wsRef.current && !wsRef.current.contains(e.target as Node)) setWsOpen(false)
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpenMenu(null)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [wsOpen, openMenu])

  const switchWorkspace = useCallback((ws: Workspace) => {
    setActiveWs(ws.name); setWsOpen(false)
    window.dispatchEvent(new CustomEvent('runai:switch-workspace', { detail: { name: ws.name, id: ws.id } }))
  }, [])

  const activeWsData = workspaces.find(w => w.name === activeWs) ?? workspaces[0]

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      {/* Workspace switcher */}
      <div ref={wsRef} style={{ position: 'relative', marginRight: 8 }}>
        <button onClick={() => setWsOpen(p => !p)} style={{
          display: 'flex', alignItems: 'center', gap: 5, padding: '2px 8px',
          background: wsOpen ? 'var(--os-hover)' : 'transparent', border: 'none', cursor: 'pointer',
          borderRadius: 5, color: activeWsData?.color ?? 'var(--os-accent)', fontSize: 11,
          fontWeight: 600, fontFamily: 'inherit',
        }}>
          {WORKSPACE_ICONS[activeWsData?.icon ?? 'Briefcase'] ?? <Briefcase size={11} />}
          {activeWs}
        </button>
        {wsOpen && (
          <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: 'var(--os-surface)', border: '1px solid var(--os-border)', borderRadius: 10, padding: 6, minWidth: 140, zIndex: 9999, boxShadow: 'var(--shadow-popover)' }}>
            {workspaces.map(ws => (
              <button key={ws.name} onClick={() => switchWorkspace(ws)} style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 10px',
                background: ws.name === activeWs ? 'var(--os-active)' : 'transparent', border: 'none',
                cursor: 'pointer', borderRadius: 6, color: ws.name === activeWs ? ws.color : 'var(--os-text)',
                fontSize: 12, fontFamily: 'inherit', textAlign: 'left',
              }}>
                <span style={{ color: ws.color }}>{WORKSPACE_ICONS[ws.icon] ?? <Briefcase size={11} />}</span>
                {ws.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Active window name */}
      {activeWindow && (
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--os-text)', marginRight: 8, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {activeWindow.title}
        </span>
      )}

      {/* Menu items with dropdowns */}
      <div ref={menuRef} style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        {Object.entries(menus).map(([menuName, items]) => (
          <div key={menuName} style={{ position: 'relative' }}>
            <button
              onClick={() => { setOpenMenu(p => p === menuName ? null : menuName); onMenuClick?.(menuName) }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'var(--os-hover)'
                e.currentTarget.style.color = 'var(--os-text)'
                if (openMenu && openMenu !== menuName) setOpenMenu(menuName)
              }}
              onMouseLeave={e => {
                if (openMenu !== menuName) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--os-muted)' }
              }}
              style={{ padding: '2px 10px', background: openMenu === menuName ? 'var(--os-hover)' : 'transparent', border: 'none', color: openMenu === menuName ? 'var(--os-text)' : 'var(--os-muted)', fontSize: 11, cursor: 'pointer', borderRadius: 4, fontFamily: 'inherit' }}
            >{menuName}</button>

            {openMenu === menuName && (
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: 'var(--os-surface)', border: '1px solid var(--os-border)', borderRadius: 8, padding: '4px 0', minWidth: 180, zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
                {items.map((item: MenuItem, i: number) => item.separator ? (
                  <div key={i} style={{ height: 1, background: 'var(--os-border)', margin: '3px 0' }} />
                ) : (
                  <button key={item.action} onClick={() => { setOpenMenu(null); dispatchMenuAction(item.action, activeWindowId ?? undefined) }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--os-accent)'; e.currentTarget.style.color = '#fff' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--os-text)' }}
                    style={{ width: '100%', padding: '6px 14px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--os-text)', textAlign: 'left', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{item.label}</span>
                    {item.shortcut && <span style={{ fontSize: 10, color: 'var(--os-muted)', marginLeft: 16 }}>{item.shortcut}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
